/**
 * PR-E — Parse a spoken phrase into a confirmation intent.
 *
 * Conservative: returns `null` when both OR neither lexical class matches so an
 * ambiguous utterance never silently fires a destructive single-use token.
 * Pure: no React, no network, no store.
 */

/** Normalize: lowercase, strip NFD diacritics, collapse whitespace. */
function normalize(input: string): string {
  return input.toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim()
}

/**
 * Confirm lexicon (pt-BR). Word-boundary match via `\b` so "confiro" does not
 * accidentally match "confirma".
 */
const CONFIRM_PATTERNS: RegExp[] = [
  /\bsim\b/,
  /\bconfirma\b/,
  /\bconfirmar\b/,
  /\bencerrar\b/,
  /\bconclui\b/,
  /\bconcluir\b/,
  /\bpode\b/,
  /\bok\b/,
  /\bpositivo\b/,
  /\bisso\b/,
]

/**
 * Cancel lexicon (pt-BR). "nao" covers accented "não" after NFD strip.
 */
const CANCEL_PATTERNS: RegExp[] = [
  /\bnao\b/,
  /\bcancela\b/,
  /\bcancelar\b/,
  /\bpara\b/,
  /\bparar\b/,
  /\bnegativo\b/,
  /\bdeixa\b/,
]

/**
 * Palavras de cortesia aceitas JUNTO da palavra de confirmação ("sim por
 * favor"). Qualquer outra palavra na frase a torna uma mensagem normal.
 */
const FILLER_WORDS = new Set(['por', 'favor'])

/** Teto de palavras: confirmação é resposta curta, não frase com conteúdo. */
const MAX_WORDS = 4

function lexiconOf(patterns: RegExp[]): Set<string> {
  return new Set(patterns.map((re) => re.source.replace(/\\b/g, '')))
}
const CONFIRM_WORDS = lexiconOf(CONFIRM_PATTERNS)
const CANCEL_WORDS = lexiconOf(CANCEL_PATTERNS)

/**
 * Map a spoken confirmation phrase to `'confirm'`, `'cancel'`, or `null`.
 *
 * A frase INTEIRA precisa ser a resposta de confirmação: só palavras do léxico
 * (mais "por favor"), no máximo {@link MAX_WORDS} palavras. Uma pergunta que
 * apenas CONTÉM "pode"/"isso"/"para" ("pode me mostrar as ordens abertas?")
 * é mensagem normal e devolve `null` — nunca dispara o token de uso único.
 *
 * Returns `null` when:
 * - The phrase is empty / whitespace only.
 * - The phrase has any word outside the lexicon (a normal message).
 * - BOTH classes match (ambiguous — fail-closed, no action taken).
 */
export function parseConfirmIntent(phrase: string): 'confirm' | 'cancel' | null {
  if (!phrase) return null
  const norm = normalize(phrase)
  if (!norm) return null

  const words = norm.split(/[^a-z0-9]+/).filter(Boolean)
  if (words.length === 0 || words.length > MAX_WORDS) return null

  let hasConfirm = false
  let hasCancel = false
  for (const w of words) {
    if (CONFIRM_WORDS.has(w)) hasConfirm = true
    else if (CANCEL_WORDS.has(w)) hasCancel = true
    else if (!FILLER_WORDS.has(w)) return null
  }

  if (hasConfirm && !hasCancel) return 'confirm'
  if (hasCancel && !hasConfirm) return 'cancel'
  // Both or neither → ambiguous → null (fail-closed)
  return null
}
